<script lang="ts">
  import { _ } from 'svelte-i18n';
  import ActivityMap from '@/Activities/ActivityMap.svelte';
  import { kmToMile } from '@/Util/utils';

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // actual_event_or_race_result of a report: the activity recorded on the target date, separate from the modeled readiness.
  export let result: any;
  export let programType: string;
  export let forUser: string | null = null;

  //@ts-ignore
  const imperial = window.Xert?._displayUnits === 'imperial';
  let polyline: [number, number][] | null = null;

  $: activity = result?.activity;
  $: others = result?.other_candidates?.length ?? 0;

  // The route of the activity shown; read again when a rebuild picks another activity, and an answer for an earlier one is ignored.
  let routeFor: string | null = null;
  async function loadRoute(id: string | undefined, athlete: string | null) {
    polyline = null;
    routeFor = id ?? null;
    if (!id) return;
    try {
      const response = await window.fetch(`/plan-route/${encodeURIComponent(id)}${athlete ? `?${new URLSearchParams({ forUser: athlete })}` : ''}`, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (routeFor !== id) return;
      if (response.ok) {
        const data = await response.json();
        if (routeFor === id) polyline = data.polyline?.length > 1 ? data.polyline : null;
      }
    } catch (e) {
      if (routeFor === id) polyline = null;
    }
  }
  $: loadRoute(activity?.activity_id, forUser);

  const time = (s: number | null | undefined) => (s ? `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(Math.round(s % 60)).padStart(2, '0')}` : '–');
  const num = (v: number | null | undefined, digits = 0) => (v === null || v === undefined ? '–' : v.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits }));
  $: distance = activity?.distance_km ? (imperial ? kmToMile(activity.distance_km) : activity.distance_km) : null;
  $: elevation = activity?.elevation_m !== null && activity?.elevation_m !== undefined ? (imperial ? activity.elevation_m * 3.28084 : activity.elevation_m) : null;
  $: speed = activity?.distance_km && (activity?.moving_time || activity?.duration) ? (imperial ? kmToMile(activity.distance_km) : activity.distance_km) / ((activity.moving_time || activity.duration) / 3600) : null;
  // The day of the report (the athlete's own local day), not the day the start time falls on in the viewer's timezone.
  const dateText = (a: any) => (a.local_date ? new Date(a.local_date + 'T12:00:00').toLocaleDateString(undefined, { dateStyle: 'long' }) : '');
</script>

{#if result?.status === 'RECORDED' && activity}
  <p class="text-sm text-zinc-500 mb-3">
    {programType === 'RACE' ? $_('report.result.intro_race') : $_('report.result.intro_event')}
  </p>
  <div class="grid gap-4">
    <div>
      <h3 class="font-medium mb-2">{activity.name ?? $_('report.result.activity')}</h3>
      <p class="text-xs text-zinc-500 mb-2">{dateText(activity)}</p>
      <dl class="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <dt class="text-zinc-500">{$_('report.result.distance')}</dt><dd class="tabular-nums">{distance === null ? '–' : `${num(distance, 1)} ${imperial ? 'mi' : 'km'}`}</dd>
        <dt class="text-zinc-500">{$_('report.result.moving_time')}</dt><dd class="tabular-nums">{time(activity.moving_time)}</dd>
        <dt class="text-zinc-500">{$_('report.result.elapsed_time')}</dt><dd class="tabular-nums">{time(activity.duration)}</dd>
        <dt class="text-zinc-500">{$_('report.result.elevation')}</dt><dd class="tabular-nums">{elevation === null ? '–' : `${num(elevation)} ${imperial ? 'ft' : 'm'}`}</dd>
        <dt class="text-zinc-500">{$_('report.result.avg_speed')}</dt><dd class="tabular-nums">{speed === null ? '–' : `${num(speed, 1)} ${imperial ? 'mph' : 'km/h'}`}</dd>
        {#if activity.avg_power}<dt class="text-zinc-500">{$_('report.result.avg_power')}</dt><dd class="tabular-nums">{num(activity.avg_power)} W</dd>{/if}
        <dt class="text-zinc-500">{$_('report.result.xss')}</dt><dd class="tabular-nums">{num(activity.xss)}</dd>
      </dl>
      <a class="mt-3 inline-block rounded border border-zinc-300 bg-white px-3 py-1.5 text-sm text-blue-700 no-underline hover:bg-zinc-50" href="/activities/{activity.activity_id}">{programType === 'RACE' ? $_('report.result.view_race') : $_('report.result.view_event')}</a>
      {#if others > 0}<p class="mt-2 text-xs text-zinc-500">{$_('report.result.others', { values: { others, kind: programType === 'RACE' ? 'RACE' : 'EVENT', method: result.selection?.method ?? '' } })}</p>{/if}
    </div>
    {#if polyline}
      <ActivityMap mapPolyline={polyline} class="h-64 rounded" />
    {/if}
  </div>
{:else if result?.status === 'NO_QUALIFYING_ACTIVITY'}
  <p class="text-sm text-zinc-500">{$_('report.result.too_short', { values: { kind: programType === 'RACE' ? 'RACE' : 'EVENT' } })}</p>
{:else}
  <p class="text-sm text-zinc-500">{$_('report.result.none')}</p>
{/if}
